import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { LabelWidthValues } from '../../../types/common/types';
import {
  ComplexEnumListEvents,
  ComplexPinnedListItem,
  ComplexSuggestedItem,
  ErrorMessageDetails,
} from '../../../ui/gzComplexEnumList/types';

import { GzComplexEnumFilterWebComponent } from '../../../ui/gzComplexEnumFilter';
import _ from 'lodash';
import { debounce } from '../../../utils/event.utils';
import { ComplexEnumFilterListEvents } from '../../../ui/gzComplexEnumFilter/types';
import { ResponseDetails } from '../types';
import { entityIconSet } from 'apps/webcomponents/src/theme/entity-icons';

/**
 * @summary More details about this component: https://bitdefender.atlassian.net/wiki/spaces/GZDOCS/pages/1652359677/Backend+Filter+Webcomponent
 * @tag backend-enum-filter
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('backend-enum-filter')
export class BackendEnumFilterWebComponent extends LitElement {
  @property({ type: String, attribute: true })
  label = '';

  @property({ type: String, attribute: true, reflect: true })
  labelWidth = LabelWidthValues.MEDIUM;

  @property({ type: Array, attribute: true, reflect: true })
  initialPinnedItems: ComplexPinnedListItem[] = [];

  @property({ type: String, attribute: true, reflect: true })
  searchPlaceholderString = '';

  @property({ type: String, attribute: true, reflect: true })
  confirmActionButtonString = '';

  @property({ type: String, attribute: true, reflect: true })
  clearSelectionButtonString = '';

  @property({ type: String, attribute: true, reflect: true })
  noSearchResultsString = '';

  @property({ type: String, attribute: true, reflect: true })
  placeholder = '';

  @property({ type: String, attribute: true, reflect: true })
  allItemsLabel = '';

  @property({ type: Array, attribute: false, reflect: false })
  ignoredFieldsForFilters: Array<string> = [];

  /**
   * By default, the APPLY button is disabled if no options in the list are checked. Setting this property to true
   * will enable the APPLY button when no items are checked.
   */
  @property({ type: Boolean, reflect: true })
  allowSelectNone = false;

  @property({ type: Object, attribute: false })
  errorMessageDetails: ErrorMessageDetails = {} as ErrorMessageDetails;

  @property({ type: Boolean, attribute: true, reflect: true })
  autoAdjustMenuPosition = false;

  @property({ type: Number, attribute: true, reflect: true })
  searchMaxLength;

  @query('#complexEnumFilter')
  protected readonly complexEnumFilter!: GzComplexEnumFilterWebComponent;

  @property({ type: Number, attribute: true, reflect: true })
  itemsPerPage = 100;

  @property({ type: Object, attribute: true, reflect: true })
  iconSet: TemplateResult = entityIconSet;

  /**
   * Callback used to fetch data
   */
  public getRemoteDataCallback: (
    page: number,
    limit: number,
    searchString: string,
    filters: Array<any>
  ) => Promise<Array<any>> = () => {
    throw new Error('Method must be overridden');
  };

  /**
   * Converter used to convert the fetched data from backend
   */
  public convertResponseDataCallback: (response: Array<any>) => ComplexSuggestedItem[] = () => {
    throw new Error('Method must be overridden');
  };

  /**
   * A callback used to obtain icons based on items types.
   * If no need of this logic, it should return only an empty Map.
   */
  public getTypeToIconMappingCallback: () => Map<number, string> = () => {
    return new Map([]);
  };

  /**
   * A callback used to get extra filters that will be passed to the backend request.
   * If no need of extra filters, an empty array should be returned
   */
  public getExtraFiltersCallback: () => Array<any> = () => {
    return [];
  };

  /**
   * A callback used to obtain icons based on items types.
   * If no need of this logic, it should return only an empty Map.
   */
  public getLabelsCallback: (value: number) => string = () => {
    return '';
  };

  /**
   *
   * @private
   */
  private pageNo = 1;

  /**
   * If the request from backend returns no result, then another event of load more should not trigger another
   * request, because we already have no data
   * @private
   */
  private allItemsFetchedWithLoadMore = false;

  /**
   *
   * @private
   */
  private loadMoreHasErrors: boolean = false;

  /**
   * Set current request number. This is incremented automatically
   * @private
   */
  private requestNumber: number = 1;

  /**
   * Last request number should be the same as the request number. If not, it means that we have a newer request,
   * and current one should be omitted, because data is deprecated and should not be rerender
   *
   * @private
   */
  private lastRequestNumber: number = 0;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      ComplexEnumListEvents.COMPLEX_SEARCH_CHANGE,
      debounce(this.handleComplexSearchChange.bind(this), 700)
    );
    this.addEventListener(ComplexEnumListEvents.COMPLEX_SELECTION_CHANGE, this.handleComplexSelectionChange.bind(this));
    this.addEventListener(
      ComplexEnumListEvents.COMPLEX_LOAD_MORE,
      debounce(this.handleComplexLoadMore.bind(this), 150)
    );
    this.addEventListener(ComplexEnumFilterListEvents.COMPLEX_DROPDOWN_CLOSE, this.handleDropdownCloseEvent.bind(this));

    this.addEventListener(ComplexEnumFilterListEvents.COMPLEX_DROPDOWN_OPEN, this.handleDropdownOpenEvent.bind(this));
  }

  disconnectedCallback() {
    this.removeEventListener(ComplexEnumListEvents.COMPLEX_SEARCH_CHANGE, this.handleComplexSearchChange);
    this.removeEventListener(ComplexEnumListEvents.COMPLEX_SELECTION_CHANGE, this.handleComplexSelectionChange);
    this.removeEventListener(ComplexEnumListEvents.COMPLEX_LOAD_MORE, this.handleComplexLoadMore);
    this.removeEventListener(ComplexEnumFilterListEvents.COMPLEX_DROPDOWN_CLOSE, this.handleDropdownCloseEvent);
    this.removeEventListener(ComplexEnumFilterListEvents.COMPLEX_DROPDOWN_OPEN, this.handleDropdownOpenEvent);

    super.disconnectedCallback();
  }

  render() {
    return html`<gz-complex-enum-filter
      id="complexEnumFilter"
      .pinnedItems="${_.cloneDeep(this.enhanceData(this.initialPinnedItems))}"
      .searchPlaceholderString="${this.searchPlaceholderString}"
      .label="${this.label}"
      .confirmActionButtonString="${this.confirmActionButtonString}"
      .clearSelectionButtonString="${this.clearSelectionButtonString}"
      .noSearchResultsString="${this.noSearchResultsString}"
      .errorMessageDetails="${this.errorMessageDetails}"
      .allItemsLabel="${this.allItemsLabel}"
      .allowSelectNone="${this.allowSelectNone}"
      .labelWidth="${this.labelWidth}"
      .searchMaxLength="${this.searchMaxLength}"
      .ignoredFieldsForFilters="${this.ignoredFieldsForFilters}"
      .iconSetInstance="${this.iconSet}"
    ></gz-complex-enum-filter>`;
  }

  private setClassIconByType(suggestedItems) {
    return suggestedItems.map(object => {
      return { ...object, icon: this.mapIconClass(object) };
    });
  }

  private mapIconClass(object) {
    if (object.type === undefined || object.type === null) {
      return;
    }

    const typeToIconMapping = this.getTypeToIconMappingCallback();
    return typeToIconMapping.get(object?.type);
  }

  private setLabelByValue(suggestedItems) {
    if (typeof this.getLabelsCallback !== 'function') {
      return suggestedItems;
    }
    return suggestedItems.map(object => {
      return { ...object, label: this.getLabelsCallback(object.value) };
    });
  }

  private async handleComplexSelectionChange(ev: CustomEvent) {
    this.resetLoadMoreDataInformation();
  }

  private resetPageNumber() {
    this.pageNo = 1;
  }

  private resetLoadMoreDataInformation() {
    this.resetPageNumber();
    this.allItemsFetchedWithLoadMore = false;
    this.loadMoreHasErrors = false;
    this.complexEnumFilter.resetScrollLocation();
  }

  private async handleComplexSearchChange(ev: CustomEvent) {
    this.resetLoadMoreDataInformation();
    const searchString = ev.detail.searchString;

    this.complexEnumFilter.updateLoadingState(true);

    const { hasErrors, requestIsDeprecated, response } = await this.fetchData(this.pageNo, searchString);

    if (requestIsDeprecated) {
      return;
    }

    if (hasErrors) {
      this.complexEnumFilter.updateErrorState();
    } else {
      this.complexEnumFilter.updateLoadingState(false);
    }

    this.complexEnumFilter.updateSuggestedItemsForEnumList(this.enhanceData(response));
  }

  private async handleComplexLoadMore(ev: CustomEvent) {
    if (this.allItemsFetchedWithLoadMore || this.loadMoreHasErrors) {
      return;
    }

    const searchString = ev.detail.searchString;
    this.pageNo++;
    this.complexEnumFilter.updateLoadingState(true);

    const { hasErrors, requestIsDeprecated, response } = await this.fetchData(this.pageNo, searchString);

    if (requestIsDeprecated) {
      return;
    }

    if (hasErrors) {
      this.loadMoreHasErrors = true;
      this.complexEnumFilter.updateErrorState();
      return;
    }

    this.complexEnumFilter.updateLoadingState(false);

    if (response.length === 0) {
      this.allItemsFetchedWithLoadMore = true;
      return;
    }

    const currentSuggestedItems = this.complexEnumFilter.getSuggestedItemsFromEnumList();

    // If we have duplicated data (we work with dynamic data and old page could have more data (in mongo result). Thus,
    // new page (current page) could have data from old page), get new items only
    const responseWithoutExistentItems = response.filter(newItem => {
      return (
        currentSuggestedItems.filter(existentItem => {
          return existentItem.value === newItem.value;
        }).length === 0
      );
    });

    const allItems = [...currentSuggestedItems, ...responseWithoutExistentItems];
    const pinnedItems = this.complexEnumFilter.pinnedItems;

    // If all items from response already exist as pinned items, then we should stop processing other load more events,
    // because loading state will be continuously displayed until we will have new data from backend
    const responseItemsThatDoNotExistAsPinnedItems = responseWithoutExistentItems.filter(item => {
      const matchItem = pinnedItems.filter(pinnedOption => {
        return pinnedOption.value === item.value;
      });
      return matchItem.length === 0;
    });

    if (responseItemsThatDoNotExistAsPinnedItems.length === 0) {
      this.allItemsFetchedWithLoadMore = true;
      return;
    }

    this.complexEnumFilter.updateSuggestedItemsForEnumList(this.enhanceData(allItems));
  }

  /**
   * Add request number as last request number and increment request number for future use. After the request is done,
   * last request number should be the same as current request number. If not, it means another request appears and
   * current request has old data and it must be ignored, in order to not rerender old data.
   * @private
   */
  private updateRequestNumber() {
    const currentRequestNumber = this.requestNumber;
    this.lastRequestNumber = this.requestNumber;
    this.requestNumber++;

    return currentRequestNumber;
  }

  /**
   * If another request has been created, it means that current request is deprecated, data is old, we should stop
   * processing
   *
   * @param currentRequestNumber
   * @private
   */
  private isRequestDeprecated(currentRequestNumber) {
    return currentRequestNumber !== this.lastRequestNumber;
  }

  private async handleDropdownOpenEvent() {
    this.resetLoadMoreDataInformation();

    this.complexEnumFilter.updateLoadingState(true);

    const { hasErrors, requestIsDeprecated, response } = await this.fetchData(this.pageNo, '');

    if (requestIsDeprecated) {
      return;
    }

    const suggestedItemsWithoutCheckedPinnedItems = response.filter(item => {
      const matchItem = this.complexEnumFilter.pinnedItems.filter(pinnedOption => {
        return pinnedOption.value === item.value && pinnedOption.checked;
      });
      return matchItem.length === 0;
    });

    if (hasErrors) {
      this.complexEnumFilter.updateErrorState();
    } else {
      this.complexEnumFilter.updateLoadingState(false);
    }

    this.complexEnumFilter.suggestedItems = this.enhanceData(suggestedItemsWithoutCheckedPinnedItems);
  }

  private handleDropdownCloseEvent() {
    this.resetLoadMoreDataInformation();
  }

  protected enhanceData(response) {
    response = this.setLabelByValue(response);
    return this.setClassIconByType(response);
  }

  /**
   *
   * @param pageNumber
   * @param searchString
   * @protected
   */
  protected async fetchData(pageNumber, searchString = ''): Promise<ResponseDetails> {
    let requestIsDeprecated = false;
    const currentRequestNumber = this.updateRequestNumber();

    let response: ComplexSuggestedItem[] = [];
    let hasErrors = false;
    try {
      response = await this.getRemoteOptions(pageNumber, searchString);
    } catch (error) {
      hasErrors = true;
    }

    if (this.isRequestDeprecated(currentRequestNumber)) {
      requestIsDeprecated = true;
    }

    return {
      hasErrors,
      requestIsDeprecated,
      response,
    };
  }

  /**
   *
   * @param pageNo
   * @param searchString
   * @protected
   */
  protected async getRemoteOptions(pageNo, searchString: string = ''): Promise<ComplexSuggestedItem[]> {
    let convertedResponse: ComplexSuggestedItem[] = [];
    const extraFiltersForRequest = this.getExtraFiltersCallback();

    const response = await this.getRemoteDataCallback(pageNo, this.itemsPerPage, searchString, extraFiltersForRequest);
    convertedResponse = this.convertResponseDataCallback(response);

    return convertedResponse;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'backend-enum-filter': BackendEnumFilterWebComponent;
  }
}
